import {
  Button,
  Icon,
  TooltipDialog,
  QuestionSVG,
  useModal,
} from "@newsela/angelou";
import PropTypes from "prop-types";
import {
  withTrackEvent,
  objectTypes,
  actionPrefixes,
  actionNames,
} from "@newsela/snitch";

import { tooltipDialogOffset } from "@/view/constants/styles/accessibleFontSizeMapping/accessibleFontSizeMapping.style";
import ReportContentFormModal from "@/view/ReportContentFormModal";
import useCurrentArticleLevelId from "@/view/utils/useCurrentArticleLevelId";
import useHasFeature from "@/features/useHasFeature";
import { VIEW_PAGE_LUNA_AI_DESIGN } from "@/ai/constants/experiments";
import KeyTermPopoutBody from "../KeyTermPopoutBody";
import {
  closeButtonCss,
  closeIconCss,
  dialogContainerCss,
  headerCss,
  keyTermIconCss,
  rootCss,
  tooltipArrowCss,
} from "./KeyTermButton.style";

const propTypes = {
  children: PropTypes.node,
  currentFontSize: PropTypes.string,
  dataIdentifier: PropTypes.string,
  isStudent: PropTypes.bool,
  keyTerm: PropTypes.string,
  keyTermDataContentId: PropTypes.string,
  termDefinition: PropTypes.string,
  toggleVocabularySupport: PropTypes.func,
};

const TooltipDialogWithTrackEvent = withTrackEvent(TooltipDialog);

const KeyTermButton = ({
  currentFontSize,
  dataIdentifier,
  isStudent,
  keyTerm,
  termDefinition,
  toggleVocabularySupport,
  children,
  keyTermDataContentId,
}) => {
  const [showLunaAiBranding] = useHasFeature(VIEW_PAGE_LUNA_AI_DESIGN);

  const levelId = useCurrentArticleLevelId();

  const eventProperties = {
    objectType: objectTypes.articleWord,
    actionPrefix: actionPrefixes.initiate,
    actionName: actionNames.read,
    streamProperties: {
      content_type: "Article",
      description: "Open key term popout",
      content_id: levelId,
    },
  };

  const getTooltipDialogOffset = (currentFontSize) => {
    return tooltipDialogOffset[currentFontSize];
  };

  const [reportContentModalProps, setReportContentModalActive, triggerRef] =
    useModal(false);

  return (
    <div css={rootCss}>
      <TooltipDialogWithTrackEvent
        __cssFor={{
          Button: {
            root: rootCss(currentFontSize),
          },
          dialog: {
            root: dialogContainerCss(showLunaAiBranding),
            arrow: tooltipArrowCss(showLunaAiBranding),
            header: headerCss,
            Button: {
              root: closeButtonCss,
              Icon: { root: closeIconCss(currentFontSize) },
            },
          },
        }}
        buttonContents={
          <>
            <span>{children}</span>
            <Icon
              __cssFor={{ root: keyTermIconCss(currentFontSize) }}
              SvgComponent={QuestionSVG}
              isDecorative
            />
          </>
        }
        buttonProps={{
          ariaProps: { "aria-label": `${keyTerm}, key term` },
          flavor: Button.flavors.incognito,
        }}
        body={
          <KeyTermPopoutBody
            currentFontSize={currentFontSize}
            contentId={levelId}
            isStudent={isStudent}
            keyTerm={keyTerm}
            termDefinition={termDefinition}
            keyTermDataContentId={keyTermDataContentId}
          />
        }
        dataProps={{
          "data-identifier": dataIdentifier,
          "data-qa-selector": "key_term_article_popout_button",
          name: keyTerm,
        }}
        dialogAriaProps={{ "aria-label": keyTerm }}
        footer={
          !isStudent && (
            <KeyTermPopoutBody.Footer
              keyTerm={keyTerm}
              ref={triggerRef}
              onReportContent={() => setReportContentModalActive(true)}
              toggleVocabularySupport={toggleVocabularySupport}
            />
          )
        }
        header={`${!isStudent ? "subject-specific" : ""} vocabulary`}
        offset={getTooltipDialogOffset(currentFontSize)}
        tooltipBackgroundColor="WHITE"
        {...eventProperties}
      />
      <ReportContentFormModal
        contentId={keyTermDataContentId}
        reportContentModalProps={reportContentModalProps}
        reportSource="KeyTerm"
        setReportContentModalActive={setReportContentModalActive}
      />
    </div>
  );
};

KeyTermButton.propTypes = propTypes;

export default KeyTermButton;
